import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../..';
import { restoreAll } from '../../restoreAll';
import { RoleTypeEnum } from '../../../commonTypes/user.types';
import { MEMBER_NAVIGATION } from './memberNavigation';
import { OWNER_NAVIGATION } from './ownerNavigation';
import { ADMIN_NAVIGATION } from './adminNavigation';
import { TEAM_LEAD_NAVIGATION } from './teamLeadNavigation';
import { PROJECT_MANAGER_NAVIGATION } from './projectManagerNavigation';
import { LocalStorage, NAVIGATION_DROPDOWN_STATES } from '../../../util/LocalStorage';
import { diContainer } from '../../../libs/container';

export interface INavigationItem {
    keyTitle: string;
    url: string;
    icon: string;
    activeIcon: string;
    iconAlt: string;
    subMenuItems: IBaseMenuItem[];
    hasParams: boolean;
    sectionTitle: string;
    disabled: boolean;
    showDropdown?: boolean;
    featureName?: string;
}

export interface IBaseMenuItem {
    keyTitle: string;
    url: string;
    hasParams: boolean;
    disabled?: boolean;
    featureName?: string;
}

export interface NavigationState {
    items: INavigationItem[];
    mobileSideBarOpen: boolean;
    sideBarCollapsed: boolean;
}

const initialState: NavigationState = {
    items: MEMBER_NAVIGATION,
    mobileSideBarOpen: false,
    sideBarCollapsed: false,
};

interface IUpdateShowDropdownPayload {
    keyTitle: string;
    showDropdown: boolean;
}

export interface IDropdownStorageState {
    [keyTitle: string]: boolean;
}

const localStorage = diContainer.get(LocalStorage);

export const navigationSlice = createSlice({
    name: 'navigation',
    initialState,
    extraReducers: builder => builder.addCase(restoreAll, () => initialState),
    reducers: {
        setNavigation: (state, action: PayloadAction<RoleTypeEnum>) => {
            if (action.payload === RoleTypeEnum.BASIC) state.items = [];
            else if (action.payload === RoleTypeEnum.ORDINARY) state.items = MEMBER_NAVIGATION;
            else if (action.payload === RoleTypeEnum.ADMIN) state.items = ADMIN_NAVIGATION;
            else if (action.payload === RoleTypeEnum.TEAM_LEAD) state.items = TEAM_LEAD_NAVIGATION;
            else if (action.payload === RoleTypeEnum.PM) state.items = PROJECT_MANAGER_NAVIGATION;
            else state.items = OWNER_NAVIGATION;
        },

        updateMobileSideBarOpen: (state, action: PayloadAction<boolean>) => {
            state.mobileSideBarOpen = action.payload;
        },

        updateSideBarCollapsed: (state, action: PayloadAction<boolean>) => {
            state.sideBarCollapsed = action.payload;
        },

        updateShowDropdown: (state, action: PayloadAction<IUpdateShowDropdownPayload>) => {
            state.items = state.items.map(item => {
                if (item.keyTitle === action.payload.keyTitle) return { ...item, showDropdown: action.payload.showDropdown };
                return { ...item, showDropdown: false };
            });
            const newStates: IDropdownStorageState = {};
            state.items.forEach(item => {
                if (item.showDropdown !== undefined) newStates[item.keyTitle] = item.showDropdown;
            });
            localStorage.set(NAVIGATION_DROPDOWN_STATES, newStates);
        },

        closeAllDropdowns: state => {
            state.items = state.items.map(item => {
                return { ...item, showDropdown: false };
            });
            localStorage.remove(NAVIGATION_DROPDOWN_STATES);
        },
    },
});

export const { setNavigation, updateMobileSideBarOpen, updateSideBarCollapsed, updateShowDropdown, closeAllDropdowns } = navigationSlice.actions;

export const selectNavigationData = (state: RootState) => state.navigation.items;

export const selectMobileSideBar = (state: RootState) => state.navigation.mobileSideBarOpen;

export const selectSideBarCollapsed = (state: RootState) => state.navigation.sideBarCollapsed;

export default navigationSlice.reducer;
